import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { LinkedUsersManager } from "@/components/LinkedUsersManager";
import {
	useTaskLinkedUsers,
	useLinkUserToTask,
	useUnlinkUserFromTask,
} from "@/hooks/useLinkedUsers";
import { Skeleton } from "@/components/ui/skeleton";

interface TaskLinkedUsersDialogProps {
	taskId: string;
	taskName: string;
	isOpen: boolean;
	onClose: () => void;
}

export const TaskLinkedUsersDialog = ({
	taskId,
	taskName,
	isOpen,
	onClose,
}: TaskLinkedUsersDialogProps) => {
	const { t } = useTranslation();
	const { data: linkedUsers = [], isLoading } = useTaskLinkedUsers(taskId);
	const linkUserMutation = useLinkUserToTask();
	const unlinkUserMutation = useUnlinkUserFromTask();
	
	const handleLinkUser = (userId: string) => {
		linkUserMutation.mutate({ taskId, userId });
	};
	
	const handleUnlinkUser = (userId: string) => {
		unlinkUserMutation.mutate({ taskId, userId });
	};
	
	return <Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						{t("tasks.linkedUsers.title", "Linked Users")} - {taskName}
					</DialogTitle>
					<DialogDescription>
						{t(
							"tasks.linkedUsers.description",
							"Manage users who have access to view and modify this task. Linked users cannot delete the task."
						)}
					</DialogDescription>
				</DialogHeader>
				
				{isLoading ? (
					<div className="space-y-4">
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</div>
				) : (
					<LinkedUsersManager
						linkedUsers={linkedUsers}
						onLinkUser={handleLinkUser}
						onUnlinkUser={handleUnlinkUser}
						isLoading={linkUserMutation.isPending || unlinkUserMutation.isPending}
						title={t("tasks.linkedUsers.manageTitle", "Manage Access")}
						description={t(
							"tasks.linkedUsers.manageDescription",
							"Add or remove users who can collaborate on this task"
						)}
					/>
				)}
			</DialogContent>
		</Dialog>;
};
